import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReferralRouteParams } from './types/';

import { IState } from 'models/reducers/i-state.interface';

import IntroSection from 'routes/singleEvent/components/IntroSection/IntroSection';
import RewardDistributionHistorySection from 'routes/singleEvent/components/RewardDistributionHistorySection/RewardDistributionHistorySection';

import AnalyticsEventsSection from './components/AnalyticsEventsSection/AnalyticsEventsSection';
import { AnalyticsParticipationCriteria } from './components/AnalyticsParticipationCriteria';
import { JoinInformational } from './components/JoinInformational';
import { ParticipationButton } from './components/ParticipationButton';
import { SectionTitle } from './components/SectionTitle';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import Spacing from 'components/spacing/Spacing';

import { useEventRegistration } from './hooks/useEventRegistration';
import { useEventTypeRedirect } from './hooks/useEventTypeRedirect';
import { useReferralEvent } from './hooks/useReferralEvent';
import { useHomePageNavigate } from 'hooks/useHomePageNavigate';
import { useScroll } from 'hooks/useScroll';

import { REFERRAL_CACHE_KEYS } from './constants';
import { getParticipationStatus } from './helpers/map-user-status.helper';

const InformationalRoute = () => {
  const { id } = useParams<ReferralRouteParams>();
  const scrollTo = useScroll('scrollbar');
  const { data, analyticsEvents, isLoading, userStatus, eventData } = useReferralEvent({
    eventTypeFilter: 'all',
    filterUpcomingOnly: true,
    sortByStartDate: 'asc',
    skipUserMetrics: true,
  });
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const { navigateToHomePage } = useHomePageNavigate();
  const queryClient = useQueryClient();

  const informationalEvent = eventData?.informational_event;
  const hasRegistration = informationalEvent?.has_registration ?? false;
  const hasPrize = !!informationalEvent?.informational_prize;
  const hasLink = !!informationalEvent?.informational_link;
  const hasEventDetails = hasLink || hasPrize;

  // Filter out registration stages from timeline when has_registration is false
  const filteredStages = useMemo(() => {
    if (!data?.stages) return [];
    if (hasRegistration) return data.stages;
    return data.stages.filter(
      (stage) => stage.name !== 'REGISTRATION STARTS' && stage.name !== 'REGISTRATION ENDS'
    );
  }, [data?.stages, hasRegistration]);

  const { mutate: registerForEvent, isPending: isRegistering } = useEventRegistration({
    eventId: id!,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.REFERRAL_EVENT, id] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_METRICS, id] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_STATUS, id] });
      setRegistrationError(null);
    },
    onError: (message) => {
      setRegistrationError(message);
    },
  });

  const participationStatus = useMemo(() => {
    return getParticipationStatus(data?.status, registrationError, userStatus, walletAddress);
  }, [data?.status, registrationError, userStatus, walletAddress]);

  const onClickRegister = () => {
    registerForEvent();
  };

  const onClickJoinNow = () => {
    setTimeout(() => {
      scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  useEffect(() => {
    if (!data && !isLoading) {
      navigateToHomePage();
    }
  }, [data, isLoading, navigateToHomePage]);

  useEffect(() => {
    scrollTo({ top: 0, behavior: 'auto' });
  }, [id, scrollTo]);

  useEventTypeRedirect({
    eventId: id,
    eventType: data?.eventType,
    currentRoute: 'informational',
    isLoading,
  });

  return (
    <Page
      className="tw-overflow-x-hidden tw-bg-[#1E1E1E] tw-text-center tw-font-jost"
      pageBackground="dark"
      requireTermsOfServiceConsents
      loading={isLoading}
    >
      <Scrollbars className="scrollbar" style={{ width: '100%', height: '100%' }}>
        <PageWrapper homeFooter={false} landingFooter variant="referral" className="tw-bg-[#101010]">
          {/* Intro section */}
          {data && (
            <IntroSection
              name={data.name}
              tagline={data.tagline}
              iconUrl={data.iconUrl}
              bannerUrl={data.bannerUrl}
              endAt={data.endAt}
              stages={filteredStages}
              variant="referral"
              customGradient="rgba(16, 16, 16, 1)"
              eventInfo={{
                eventId: id!,
                walletAddress,
                userStatus,
              }}
            >
              {hasRegistration && (
                <div className="tw-mt-[40px] tw-flex tw-flex-col tw-items-center tw-gap-[16px]">
                  <ParticipationButton
                    participationStatus={participationStatus}
                    isRegistering={isRegistering}
                    onClickRegister={onClickRegister}
                    registrationError={registrationError}
                    eligibilityReasons={userStatus?.eligibility?.reasons}
                    hasWallet={!!walletAddress}
                  />
                </div>
              )}
            </IntroSection>
          )}

          {/* Event details section — only if there's a link or prize to show */}
          {hasEventDetails && eventData && (
            <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-bg-[#101010] tw-py-[50px] lg:tw-px-0">
              <div className="tw-relative tw-mx-auto tw-w-full tw-max-w-[1280px]">
                <JoinInformational
                  informationalEvent={informationalEvent}
                  registrationEndDatetime={eventData.registration_end_datetime}
                  startDatetime={eventData.start_datetime}
                />
              </div>
            </div>
          )}

          {/* Other events section */}
          {analyticsEvents.length > 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-bg-[#101010] tw-px-[20px] tw-py-[40px] lg:tw-px-0 lg:tw-pt-[50px]">
              <SectionTitle>Check Out Our Other Events</SectionTitle>
              <Spacing height={40} />
              <AnalyticsEventsSection analyticsEvents={analyticsEvents} />
            </div>
          )}

          {/* Reward distribution history — only if prize exists */}
          {hasPrize && <RewardDistributionHistorySection eventId={id} />}

          {/* Participation criteria section */}
          {data && data.participationCriteria.length > 0 && (
            <AnalyticsParticipationCriteria
              participationCriteria={data.participationCriteria}
              onClickJoinNow={onClickJoinNow}
            />
          )}
        </PageWrapper>
      </Scrollbars>
    </Page>
  );
};

export default InformationalRoute;
